import React, { ReactNode, useCallback } from "react";

import cn from "classnames";
import { Link } from "react-router-dom";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import Path from "@app/enum/Path";
import styles from "@app/scene/dashboard/components/Sidebar/components/ScopeList/components/ScopeItem.module.scss";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

export interface ScopeItemProps {
  className?: string;
  clickable?: boolean;
  defaultScope?: boolean;
  isManager?: boolean; // purely for analytics
  isOwner?: boolean; // purely for analytics
  label: ReactNode;
  prefix?: ReactNode;
  selected?: boolean;
  suffix?: ReactNode;
  teamId?: Team["id"];
  topLevel?: boolean;
  userId?: User["id"];
  view: ViewType;
}

const ScopeItem: React.FC<ScopeItemProps> = ({
  className,
  clickable = true,
  defaultScope,
  isManager,
  isOwner,
  label,
  prefix,
  selected,
  suffix,
  teamId,
  topLevel,
  userId,
  view,
}) => {
  const analytics = useExtendedAnalytics("Row", useAnalytics());
  const isTopLevel = topLevel ?? !userId;

  const handleClick = useCallback(() => {
    let scopeLabel = "Unknown";
    if (!teamId && !userId) {
      scopeLabel = "Org";
    } else if (teamId && !userId) {
      scopeLabel = "Team";
    } else if (teamId && userId) {
      scopeLabel = isManager ? "Manager" : "Rep";
    } else if (!teamId && userId) {
      scopeLabel = isOwner ? "Owner" : "Rep";
    }
    analytics.clicked([scopeLabel]);
  }, [analytics, teamId, userId, isManager, isOwner]);

  return clickable ? (
    <Link
      className={cn(styles.container, className, {
        "mmc-js-default-scope": defaultScope,
        [styles.selected]: selected,
        [styles.subItem]: !isTopLevel,
      })}
      onClick={handleClick}
      to={`${Path.DASHBOARD}${teamId ? `/teams/${teamId}` : ""}${userId ? `/users/${userId}` : ""}${
        view !== ViewType.DASHBOARD ? `/${view}` : ""
      }`}
    >
      {prefix}
      {label}
      {suffix}
    </Link>
  ) : (
    <div className={cn(styles.container, styles.staticText)}>
      {prefix}
      {label}
      {suffix}
    </div>
  );
};

export default ScopeItem;
